// vim: tabstop=2:shiftwidth=2:expandtab:
import * as olExtent from 'ol/extent';
import Feature from 'ol/Feature';
import {Circle, Point, LineString} from 'ol/geom';
import {modulo} from 'ol/math.js';
import {padNumber} from 'ol/string.js';
import {Fill, Icon, Stroke, Style, Text} from 'ol/style';

const net_epoch_ticks = 621355968000000000;

const const_NetUtcStart = (new Date(1970, 1, 1)).getTime();

/// Get .NET timestamp.
export function getTimestampNetUtc() {
  const now = new Date();
  const diff = now.getTime()*10000 + net_epoch_ticks;
  return diff;
}

/// Get .NET timestamp from the milliseconds system time.
export function getTimestampNetUtcFromMs(time_ms) {
  const diff = time_ms*10000 + net_epoch_ticks;
  return diff;
}

/// Get Javascript date from the UTC timestamp.
export function getDateFromTicksNetUtc(ticksNetUtc) {
  const time = (ticksNetUtc - net_epoch_ticks)* 0.0001;
  const r = new Date();
  r.setTime(time);
  return r;
}

export function getJavascriptTimeFromTicksNetUtc(ticksNetUtc) {
  const time = (ticksNetUtc - net_epoch_ticks)* 0.0001;
  return time;
}

export function hoursMinutesTextOfSeconds(seconds) {
  const m = Math.floor(seconds / 60);
  const hours = Math.floor(m / 60);
  const minutes = m % 60;
  return `${hours}h:${minutes}m`;
}

/// Format depth with the required number of decimals.
export function depthTextOf(x, ndecimals) {
  if (typeof x == 'number') {
    const multiplier = Math.round(Math.pow(10, ndecimals));
    const iz = Math.trunc(-multiplier * x);
    const r = `${Math.trunc(iz / multiplier)}.${(iz % multiplier).toString().padStart(ndecimals, '0')}`;
    return r;
  }
  else if (typeof x == 'string') {
    return x;
  }
}

export const EXPIREDAYS_COOKIE = 2 * 365; // Two years for the cookie to expire.

/// Set a cookie.
/// If exdays is not set, EXPIREDAYS_COOKIE will be used.
export function setCookie(cname, cvalue, exdays) {
  var d = new Date();
  if (!exdays) {
    exdays = EXPIREDAYS_COOKIE;
  }
  d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
  var expires = "expires="+d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

/// Get a cookie.
/// Returns an empty string when not set.
export function getCookie(cname) {
  var name = cname + "=";
  var ca = document.cookie.split(';');
  for(var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

/**
 * @param {string} hemispheres Hemispheres.
 * @param {number} degrees Degrees.
 * @param {number=} opt_fractionDigits The number of digits to include
 *    after the decimal point. Default is `0`.
 * @return {string} String.
 */
function degreesToStringHDM(hemispheres, degrees, opt_fractionDigits) {
  var normalizedDegrees = modulo(degrees + 180, 360) - 180;
  var x = Math.abs(60 * normalizedDegrees);
  var dflPrecision = opt_fractionDigits || 0;
  var precision = Math.pow(10, dflPrecision);
  var deg = Math.floor(x / 60);
  var min = x - (deg * 60);
  min = Math.ceil(min * precision) / precision;
  if (min >= 60) {
      min = 0;
      deg += 1;
  }
  return deg + '\u00b0 ' + 
      padNumber(min, 2, dflPrecision) + '\u2032' +
      (normalizedDegrees == 0 ? '' : ' ' + hemispheres.charAt(normalizedDegrees < 0 ? 1 : 0));
}

export function latitudeToHDM(latitude, opt_fractionDigits) {
  return degreesToStringHDM('NS', latitude, opt_fractionDigits);
}

export function longitudeToHDM(longitude, opt_fractionDigits) {
  return degreesToStringHDM('EW', longitude, opt_fractionDigits);
}

/// @param coordinate Array consisting of longitude and latitude.
/// @param opt_fractionDigits Number of digits.
export function toStringHDM(coordinate, opt_fractionDigits) {
  return degreesToStringHDM('NS', coordinate[1], opt_fractionDigits) + ' ' + degreesToStringHDM('EW', coordinate[0], opt_fractionDigits);
}


/// @param {string} hdm Coordinate in the form of "58° 02.69507′ N"
/// @return {float} One of the coordinates.
export function parseHDM(hdm) {
  const is_negative = hdm.endsWith("S") || hdm.endsWith("W");
  const v = hdm.split(/ |N|W|S|E|\u00b0|\u2032+/).filter(s => s).map(s => s.replace(",", "."));
  const r = v.length>1 ? parseFloat(v[0]) + parseFloat(v[1])/60.0 : parseFloat(v[0]);
  return is_negative ? (-r) : r;
}

export function parseIniString(data){
  var regex = {
      section: /^\s*\[\s*([^\]]*)\s*\]\s*$/,
      param: /^\s*([^=]+?)\s*=\s*(.*?)\s*$/,
      comment: /^\s*;.*$/
  };
  var value = {};
  var lines = data.split(/[\r\n]+/);
  var section = null;
  lines.forEach(function(line){
      if(regex.comment.test(line)){
          return;
      }else if(regex.param.test(line)){
          var match = line.match(regex.param);
          if(section){
              value[section][match[1]] = match[2];
          }else{
              value[match[1]] = match[2];
          }
      }else if(regex.section.test(line)){
          var match = line.match(regex.section);
          value[match[1]] = {};
          section = match[1];
      }else if(line.length == 0 && section){
          section = null;
      };
  });
  return value;
}

export function centerOfFeatures(features) {
	let ext = null;
	for (let i=0; i<features.length; ++i) {
		const f = features[i];
		const ext_i = f.getGeometry().getExtent();
		if (ext == null) {
			ext = ext_i;
		}
		else {
			ext = olExtent.extend(ext, ext_i);
		}
	}
	const center = [(ext[0] + ext[2])/2, (ext[1] + ext[3])/2]
	return center;
}

/// Parse float, substitute '.' for ',' beforehand.
export function parseFloatWithComma(s : string) : number | undefined
{
  return parseFloat(s.replace(',', '.'));
}

export function isEmptyObject(o) {
  for (let x in o) {
    return false;
  }
  return true;
}

function processEventHandlers(lst) {
  if (!lst) {
    return;
  }
  for (let i=0; i<lst.length; ++i) {
    try {
      lst[i]();
    }
    catch (error) {
      console.log(`Event handler problem: ${error}`);
    }
  }
}

/// WebSocket that automatically:
/// a) Parses/Pretty-prints objects in WebSocket text messages.
/// b) Reconnects after the given time.
/// c) convenience methods.
/// TODO: reconnect when no messages received in some time.
/// TODO: ping message.
export class JsonWebsocket {
  #serverUrl
  #loginMessage
  #reconnectTimeSec
  #connection
  #listeners
  #eventlisteners
  #processors

/// Reconnect
  reconnect() {
    const self = this;
    this.#connection = new WebSocket(this.#serverUrl);
    // Connection opened
    this.#connection.addEventListener('open', function (event) {
        console.log('Server connection opened!');
        processEventHandlers(self.#eventlisteners['open']);
        if (self.#loginMessage) {
            self.sendMessage(self.#loginMessage);
        }
    });
    // Connection closed.
    this.#connection.addEventListener('close', function(event) {
        self.#connection = null;
        console.log(`Server connection closed, will be re-opened in ${self.#reconnectTimeSec} seconds!`);
        processEventHandlers(self.#eventlisteners['close']);
        window.setTimeout(() => self.reconnect(), self.#reconnectTimeSec * 1000);
    });
    // Listen for messages
    this.#connection.addEventListener('message', (event) => {
      const message = JSON.parse(event.data);
      let new_request = {};
      self.#listeners.forEach((h) => h(message, new_request));
      if (!isEmptyObject(new_request)) {
        self.sendMessage(new_request);
      }
      for (let key in message) {
        const lst = self.#processors[key];
        if (!lst) {
          continue;
        }
        const h = lst.shift();
        if (!h) {
          continue;
        }
        const v = message[key];
        h(v);
      }
    });
  }

  /// Send a JSON object to the server when connection is present.
  sendMessage(obj) {
    try {
      const c = this.#connection;
      if (c && c.readyState == WebSocket.OPEN) {
        c.send(JSON.stringify(obj));
      }
    }
    catch (ex) {
      console.log('WebSocket.Send: ' + ex);
    }
  }

  /// Add a handler that will receive all JSON messages.
  addJsonListener(handler) {
      this.#listeners.push(handler)
  }

  addEventListener(type, handler) {
    let lst = this.#eventlisteners[type];
    if (lst) {
      lst.push(handler);
    }
    else {
      this.#eventlisteners[type] = [handler];
    }
  }

  /// TODO: test this method.
  /// Send and receive an object using the same JSON name.
  transceive(key, obj, responseHandler) {
    var pl = this.#processors[key];
    if (pl) {
        pl.push(responseHandler);
    }
    else {
        this.#processors[key] = [ responseHandler ];
    }
    let message_to_send = {};
    message_to_send[key] = obj;
    this.sendMessage(message_to_send);
  }

  /// Log a message to the console and to the server, too.
  log(message) {
    console.log(message);
    this.sendMessage({ConsoleLog: message});
  }

  /// options:
  ///    serverUrl
  ///    loginMessage
  ///    reconnectTimeSec - time before reconnect, in seconds.
  constructor(options) {
      this.#serverUrl =  options.serverUrl;
      this.#loginMessage = options.loginMessage;
      this.#reconnectTimeSec =  options.reconnectTimeSec ? options.reconnectTimeSec : 60; // 1 minute timeout by default.
      this.#connection = null;
      this.#listeners = [];
      this.#eventlisteners = {};
      this.#processors = {};
  }
}

const myTracks_LineStringStyle = new Style({
  stroke: new Stroke({
    color: 'lightblue',
    width: 3
  })
});

export class MyTracks {
  #isTracking
  #track
  #source
  #feature
  #allFeatures
  #storage
  #currentIndex
  #currentName

  #nameOfIndex(i) {
    return "track" + i.toString();
  }

  #addFeature(track) {
    const f = new Feature({
      geometry: new LineString(track),
    });
    f.set('style', myTracks_LineStringStyle);
    this.#source.addFeature(f);
    this.#allFeatures.push(f);
    return f;
  }

  isTracking() {
    return this.#isTracking;
  }

  /// Start tracking.
  start() {
    this.#isTracking = true;
    this.#track = [];
    this.#currentName = null;
    this.#feature = this.#addFeature(this.#track);
  }

  /// Stop the tracking.
  stop() {
    // FIXME: do what?
    this.#isTracking = false;
  }

  clear() {
    const was_tracking = this.#isTracking;
    this.stop();
    this.#allFeatures.forEach(el => this.#source.removeFeature(el));
    this.#allFeatures.length = 0;
    const storage = this.#storage;
    for (let i=storage.length-1; i>=0; --i) {
      const key = storage.key(i);
      if (key.startsWith("track")) {
        storage.removeItem(key);
      }
    }
    if (was_tracking) {
      this.start();
    }
  }

  /// Add a point.
  addPoint(location) {
    if (!this.#isTracking) {
      return;
    }
    const g = this.#feature.getGeometry();
    g.appendCoordinate(location);
    this.#track.push(location);
    if (!this.#currentName) {
      for (let i = this.#currentIndex; ; ++i) {
        const key = this.#nameOfIndex(i);
        const s = this.#storage.getItem(key);
        if (!s) {
          this.#currentIndex = i;
          this.#currentName = this.#nameOfIndex(i);
          break;
        }
      }
    }
    this.#storage.setItem(this.#currentName,  JSON.stringify(this.#track));
  }

  constructor(source, storage) {
    this.#isTracking = false;
    this.#source = source;
    this.#track = [];
    this.#feature = null;
    this.#allFeatures = [];
    this.#storage = storage;
    this.#currentIndex = 0; // have to find out :)
    this.#currentName = null;

    // load the storage, if any.
    for (let i=0; i<storage.length; ++i) {
      const key = storage.key(i);
      if (key.startsWith("track")) {
        const s = storage.getItem(key);
        const track = JSON.parse(s);
        if (track && track.length>0) {
          this.#addFeature(track);
        }
      }
    }
  }
}

function normalize (strArray) {
  // from https://github.com/jfromaniello/url-join/blob/master/lib/url-join.js
  let resultArray = [];
  if (strArray.length === 0) { return ''; }

  if (typeof strArray[0] !== 'string') {
    throw new TypeError('Url must be a string. Received ' + strArray[0]);
  }

  // If the first part is a plain protocol, we combine it with the next part.
  if (strArray[0].match(/^[^/:]+:\/*$/) && strArray.length > 1) {
    const first = strArray.shift();
    strArray[0] = first + strArray[0];
  }

  // There must be two or three slashes in the file protocol, two slashes in anything else.
  if (strArray[0].match(/^file:\/\/\//)) {
    strArray[0] = strArray[0].replace(/^([^/:]+):\/*/, '$1:///');
  } else {
    strArray[0] = strArray[0].replace(/^([^/:]+):\/*/, '$1://');
  }

  let dotdot_count = 0;
  for (let i = strArray.length-1; i >= 0; --i) {
    let component = strArray[i];

    if (typeof component !== 'string') {
      throw new TypeError('Url must be a string. Received ' + component);
    }

    if (component === '') {
      continue;
    }

    if (i > 0) {
      // Removing the starting slashes for each component but the first.
      component = component.replace(/^[\/]+/, '');
    }
    if (i < strArray.length - 1) {
      // Removing the ending slashes for each component but the last.
      component = component.replace(/[\/]+$/, '');
    } else {
      // For the last component we will combine multiple slashes to a single one.
      component = component.replace(/[\/]+$/, '/');
    }

    if (component == '.') {
      continue;
    }
    if (component == '..') {
      ++dotdot_count;
      continue;
    }

    if (dotdot_count > 0) {
      --dotdot_count;
      continue;
    }
    resultArray.unshift(component);
  }

  let str = resultArray.join('/');
  // Each input component is now separated by a single slash except the possible first plain protocol part.

  // remove trailing slash before parameters or hash
  str = str.replace(/\/(\?|&|#[^!])/g, '$1');

  // replace ? in parameters with &
  let parts = str.split('?');
  str = parts.shift() + (parts.length > 0 ? '?': '') + parts.join('&');

  return str;
}

export function joinUrl() {
  let input = null;

  if (typeof arguments[0] === 'object') {
    input = arguments[0];
  } else {
    input = [].slice.call(arguments);
  }

  let real_input = [input[0]];
  for (let i=1; i<input.length; ++i) {
    if (i+1 < input.length) {
      real_input = real_input.concat(input[i].split('/'));
    }
    else {
      real_input.push(input[i]);
    }
  }

  return normalize(real_input);
}

/// Uploader helper.
export class Uploader {
  div: HTMLElement;
  files: string[];

  constructor(div) {
      this.div = div;
      this.files = [];
      this.div.style.opacity = "1";
      this.div.innerText = 'No uploads (yet)'
  }

  startUpload(path, contents) {
      console.log(`Starting download ${path}`);
  }
}

export function createUploader()
{
  return 0;
}

function dateToYyyyHhMm(date : Date) : string
{
  const iso = date.toISOString(); // 2021-03-14
  const r = iso.substring(0,4) + iso.substring(5,7) + iso.substring(8,10);
  return r;
}

interface NamedIndex {
  Name : string;
  Index: number;
}

interface TrackLogIndices {
  TimestampJs : number;
  Latitude : number;
  Longitude : number;
  Z : number;
  SpeedKnots : number;
  StatusTimeJs : number;
  Status : number;
  PositionKm : number;
  Headings : { [id: string]: number; };
}

function getTrackLogIndices(header : string[], headingFields : string[]) : TrackLogIndices
{
  const indices : TrackLogIndices = {
    TimestampJs : header.indexOf('TimestampJS'),
    Latitude : header.indexOf('Latitude'),
    Longitude : header.indexOf('Longitude'),
    Z : header.indexOf('Z'),
    SpeedKnots : header.indexOf('SpeedKnots'),
    StatusTimeJs : header.indexOf('StatusTimeJS'),
    Status : header.indexOf('Status'),
    PositionKm : header.indexOf('PositionKm'),
    Headings : {}
  };
  headingFields.forEach(fieldName => indices.Headings[fieldName] = header.indexOf(fieldName));
  // headingFields.map(fieldName => ({ Name:  fieldName, Index: header.indexOf(fieldName) }))
  return indices;
}

export interface TrackPoint {
  TimestampJs : number;
  LocationWeb : number[];
  Z? : number;
  SpeedKnots? : number;
  StatusTimeJs? : number;
  Status? : string;
  PositionKm? : number;
}

function parseTrackLogLine(line : string[], indices : TrackLogIndices, transform : (x : number[])=>number[], startTimestamp : number) : TrackPoint
{
  const ts = parseInt(line[indices.TimestampJs]);
  if (isNaN(ts) || ts < startTimestamp) {
    return null;
  }
  const s_lon = line[indices.Longitude];
  const s_lat = line[indices.Latitude];
  if (!s_lon || !s_lat || s_lon=="0" || s_lat=="0") {
    return null;
  }
  const lon_lat =  [ parseFloat(s_lon), parseFloat(s_lat) ];
  if (isNaN(lon_lat[0]) || isNaN(lon_lat[1])) {
    return null;
  }
  const web_location = transform(lon_lat);
  const r : TrackPoint = {
    TimestampJs : ts,
    LocationWeb : web_location,
  };

  const s_z = line[indices.Z];
  if (s_z) {
    r.Z = parseFloat(s_z);
  }

  const s_speed_knots = line[indices.SpeedKnots];
  if (s_speed_knots) {
    r.SpeedKnots = parseFloat(s_speed_knots);
  }

  const s_status_time_js = line[indices.SpeedKnots];
  if (s_status_time_js) {
    r.StatusTimeJs = parseInt(s_status_time_js);
  }

  const s_status = line[indices.Status];
  if (s_status) {
    r.Status = s_status;
  }

  const s_position_km = line[indices.PositionKm];
  if (s_position_km) {
    r.PositionKm = parseFloat(s_position_km);
  }

  for (let name in indices.Headings) {
    const index = indices.Headings[name];
    const s = line[index];
    if (s) {
      r[name] = parseFloat(s);
    }
  }
  return r;
}

export async function fetchTrackLogFeatures(baseUrl : string, vesselName : string, headingFields : string[], datasetVersion : string, transform : (latlon : number[]) => number[], startTime : Date, nowTime : Date)
{
  const fn = dateToYyyyHhMm(nowTime) + ".txt";
  const response = await fetch(baseUrl + fn + "?v=" + datasetVersion);
  const all_text = await response.text();
  const allTextLines = all_text.split(/\r\n|\n/);
  const line0 = allTextLines[0];
  if (line0.startsWith('<!DOCTYPE html>')) {
    return null;
  }
  const headers = line0.split(',');
  const features = [];

  // Indices.
  const indices = getTrackLogIndices(headers, headingFields);

  const start_timestamp = startTime.getTime();
  let last_status = null;
  let feature_track = []; // points for a given feature.
  const TRACK_INTERVAL_MS = 10 * 1000;
  let next_timestamp_ms = 0;
  for (var i=1; i<allTextLines.length; i++) {
    var line = allTextLines[i].split(',');
    if (line.length < headers.length) {
      continue;
    }
    var tp = parseTrackLogLine(line, indices, transform, start_timestamp);
    if (!tp) {
      continue;
    }
    if (tp.TimestampJs < next_timestamp_ms && i+1<allTextLines.length) {
      continue;
    }
    next_timestamp_ms = tp.TimestampJs + TRACK_INTERVAL_MS;
    if (last_status && last_status != tp.Status) {
      if (feature_track.length>0) {
        const nf = new Feature({
          geometry: new LineString(feature_track.map(ft => ft.LocationWeb)),
          vesselName: vesselName,
          track: feature_track,
        });
        features.push(nf);
      }
      feature_track = [];
    }
    feature_track.push(tp);
    last_status = tp.Status;
  }
  if (feature_track.length>0) {
    const nf = new Feature({
      geometry: new LineString(feature_track.map(ft => ft.LocationWeb)),
      vesselName: vesselName,
      track: feature_track,
    });
    features.push(nf);
  }

  return features;
}

export function createTrackLogFeatures(vesselName)
{
  const nf = new Feature({
    geometry: new LineString([]),
    vesselName: vesselName,
    track: [],
  });
  return [ nf ];
}

/// Download the given data by the web user.
export function downloadByUser(data, filename, type) {
  var file = new Blob([data], {type: type});
  const nav = (window.navigator as any);
  if (nav.msSaveOrOpenBlob) // IE10+
      nav.msSaveOrOpenBlob(file, filename);
  else { // Others
      var a = document.createElement("a"),
              url = URL.createObjectURL(file);
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(function() {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
      }, 0);
  }
}

/// Read the file given by the input file control (fileControl.files[0]).
/// Use await to fetch the results.
export function readLocalFile(fileControl) {
  return new Promise((resolve, reject) => {
    let fr = new FileReader();
    fr.onload = x => resolve(fr.result);
	fr.onerror = x => reject(x);
    fr.readAsText(fileControl.files[0]);
})}

/// Parse the NMEA coordinate string in the given quadrant.
function parseCoordinate(stringDegrees, quadrant) {
  let dotpos = stringDegrees.indexOf('.');
  let deg = Number.parseFloat(stringDegrees.slice(0, dotpos-2));
  let minutes = Number.parseFloat(stringDegrees.slice(dotpos-2));
  let r = deg + minutes / 60;
  if (quadrant == 'S' || quadrant == 'W') {
    return -r;
  }
  return r;
}

export interface WebUserLogPoint {
  timeMs: number;
  headingDeg?: number;
  positionLocal?: number[];
}

/// Parse log file content into an array of log file records of the form: { timeMs:, headingDeg:, positionLocal:[]}
export function parseWebUserLog(logfileContent : string, toLocalCoordinates : (webcoordinates : number[]) => number[]) : WebUserLogPoint[]
{
  let r = [];
  let lines = logfileContent.split(/[\r\n]+/);
  lines.forEach(line => {
    if (line==null || line.length==0) {
      return;
    }
    try {
      let ts_nmea = line.split(':');
      if (ts_nmea.length < 2) {
        return;
      }
      let ts_net = Number.parseInt(ts_nmea[0], 10);
      let item : WebUserLogPoint = {
        timeMs : getJavascriptTimeFromTicksNetUtc(ts_net)
      };
      let nmea_parts = ts_nmea[1].split(',');
      let n0 = nmea_parts[0];
      if (n0.endsWith('HDT')) {
        // must be compass heading.
        item.headingDeg = Number.parseFloat(nmea_parts[1]);
        r.push(item);
      }
      else if (n0.endsWith('GGA')) {
        let lat = parseCoordinate(nmea_parts[2], nmea_parts[3]);
        let lon = parseCoordinate(nmea_parts[4], nmea_parts[5]);
        item.positionLocal = toLocalCoordinates([lon, lat]);
        r.push(item);
      }
    } catch (error) {
      console.log('parseWebUserLog:' + error + '; source line: ' + line);
    }
  });
  return r;
}
